import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import {
  DEFAULT_SEARCH_STATE,
  FilterToken,
  OWNED_URL_PARAMS,
  SEARCH_DEFAULT_DIRECTION,
  SEARCH_DEFAULT_SORT,
  SearchFilterState,
  SearchRequestArgs,
  deriveTokens,
  parseSearchState,
  serializeSearchState,
  toSearchRequest,
} from 'screens/Main/Search/searchState';
import { appendCardFilterParams } from 'store/services/cards/search/cardsSearchApi';

/**
 * URL state for the shared FilterPanel on the list surfaces (My Want List, My
 * Have List, profile tabs, Recent Activity).
 *
 * Same URL vocabulary as /search (parseSearchState / serializeSearchState), so
 * a filter written on one surface reads on any other. The one difference: the
 * list surfaces own `sort` through useSortPreference (compound `key-dir`), so
 * this module never reads or writes `sort` / `direction`. Both stay pinned to
 * the Search defaults inside the state object, which serializeSearchState then
 * omits.
 */
export const CARD_FILTER_URL_PARAMS: readonly string[] = OWNED_URL_PARAMS.filter(
  (key) => key !== 'sort' && key !== 'direction',
);

export const parseCardFilters = (params: URLSearchParams): SearchFilterState => ({
  ...parseSearchState(params),
  sort: SEARCH_DEFAULT_SORT,
  direction: SEARCH_DEFAULT_DIRECTION,
});

export const serializeCardFilters = (state: SearchFilterState): URLSearchParams =>
  serializeSearchState({ ...state, sort: SEARCH_DEFAULT_SORT, direction: SEARCH_DEFAULT_DIRECTION });

/**
 * The same wire params as a flat object, for the activity socket's
 * `activity:set_filters` payload. Built through appendCardFilterParams so the
 * socket and the REST request can never spell a key differently. `{}` when
 * nothing is set, which the server reads as "clear".
 */
export const toCardFilterWireObject = (args: SearchRequestArgs | undefined): Record<string, string> => {
  const params = new URLSearchParams();
  if (args) appendCardFilterParams(params, args);
  return Object.fromEntries(params.entries());
};

/** Request args for a list endpoint: the Search request minus sort/direction. */
export const toCardFilterRequest = (state: SearchFilterState): SearchRequestArgs | undefined => {
  const { sort: _sort, direction: _direction, ...args } = toSearchRequest(state);
  return Object.keys(args).length > 0 ? args : undefined;
};

const TEXT_DEBOUNCE_MS = 300;

export interface CardFilterStateApi {
  /** Live state, for the panel. */
  state: SearchFilterState;
  /** State to build requests from: text fields settle after 300ms, toggles apply at once. */
  requestState: SearchFilterState;
  /** Request args derived from requestState; undefined when nothing is set. */
  requestArgs: SearchRequestArgs | undefined;
  tokens: FilterToken[];
  hasActiveFilters: boolean;
  /** The filter the user set most recently, for the filtered-empty state's left action. */
  lastAddedToken: FilterToken | undefined;
  update: (patch: Partial<SearchFilterState>) => void;
  clearToken: (token: FilterToken) => void;
  clearAll: () => void;
}

const TEXT_FIELDS: (keyof SearchFilterState)[] = [
  'name',
  'oracleText',
  'typeLine',
  'setName',
  'flavorText',
  'artist',
  'manaCost',
  'cmc',
  'power',
  'toughness',
  'pointsMin',
  'pointsMax',
];

const textFieldsDiffer = (a: SearchFilterState, b: SearchFilterState): boolean =>
  TEXT_FIELDS.some((key) => a[key] !== b[key]);

export const useCardFilterState = (): CardFilterStateApi => {
  const [searchParams, setSearchParams] = useSearchParams();
  const state = useMemo(() => parseCardFilters(searchParams), [searchParams]);

  const commit = useCallback(
    (next: SearchFilterState) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          // Clear only the keys this module owns so ?tab=, ?sort= and any
          // partner/utm params survive.
          CARD_FILTER_URL_PARAMS.forEach((key) => params.delete(key));
          serializeCardFilters(next).forEach((value, key) => params.set(key, value));
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const update = useCallback(
    (patch: Partial<SearchFilterState>) => commit({ ...state, ...patch }),
    [state, commit],
  );
  const clearAll = useCallback(() => commit(DEFAULT_SEARCH_STATE), [commit]);
  const clearToken = useCallback(
    (token: FilterToken) => commit(token.clear(state)),
    [state, commit],
  );

  // Text fields settle before a request goes out; everything else is immediate.
  const [requestState, setRequestState] = useState(state);
  useEffect(() => {
    if (!textFieldsDiffer(state, requestState)) {
      setRequestState(state);
      return undefined;
    }
    const timer = setTimeout(() => setRequestState(state), TEXT_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // requestState is intentionally not a dependency: it is the value being settled.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const requestArgs = useMemo(() => toCardFilterRequest(requestState), [requestState]);
  const tokens = useMemo(() => deriveTokens(state), [state]);

  // Addition order of the active tokens, so "clear the most recent filter" is
  // honest within a session. On a fresh load the order is unknown and the last
  // derived token stands in.
  const orderRef = useRef<string[]>([]);
  const ids = tokens.map((t) => t.id);
  orderRef.current = [
    ...orderRef.current.filter((id) => ids.includes(id)),
    ...ids.filter((id) => !orderRef.current.includes(id)),
  ];
  const lastAddedId = orderRef.current[orderRef.current.length - 1];
  const lastAddedToken = tokens.find((t) => t.id === lastAddedId) ?? tokens[tokens.length - 1];

  return {
    state,
    requestState,
    requestArgs,
    tokens,
    hasActiveFilters: tokens.length > 0,
    lastAddedToken,
    update,
    clearToken,
    clearAll,
  };
};
